import { AppBar, Badge, Button, Container, IconButton, Toolbar, useMediaQuery, useTheme } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';
import StorefrontOutlinedIcon from '@material-ui/icons/StorefrontOutlined';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import MobileDrawer from 'src/components/page/MobileDrawer';
import { NavMenu } from 'src/components/page/NavMenu';
import TopBar from 'src/components/page/TopBar';
import { ROUTE_ACCOUNT } from 'src/config/account-routes';
import {
    GROUP_ABOUT,
    GROUP_SERVICES,
    ROUTE_ABOUT,
    ROUTE_ADMIN,
    ROUTE_CART,
    ROUTE_CONTACT,
    ROUTE_HOME,
    ROUTE_SERVICES,
    ROUTE_STORE,
} from 'src/config/routes';
import { AppRoute } from 'src/core/AppRoute';
import { useAccount } from 'src/core/hooks/account';
import { useAccountActionCount, useAccountRoutes } from 'src/core/hooks/account-routes';
import { useStoreRoutes } from 'src/core/hooks/store-routes';
import { useEnabledSiteModuleKeys } from 'src/core/provider/SiteProvider';
import Logo from 'src/resources/icons/Logo';
import { getShoppingCart } from 'src/store/cart/selectors';
import { getMyOffers } from 'src/store/offer/selectors';

const AppbarContainer = styled('div')({
    position: 'relative',
});

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    alignItems: 'stretch',
    padding: 0,
    margin: theme.spacing(1.2, 0),
    [theme.breakpoints.down('md')]: {
        margin: theme.spacing(0.9, 0),
    },
}));

const ToolbarItemLogoWrapper = styled(Button)(({ theme }) => ({
    '& [class*="MuiButton-root"]': {
        padding: 0,
    },
    '& svg': {
        height: 'auto',
        width: '200px',
        [theme.breakpoints.down('md')]: {
            width: '150px',
        },
    },
}));

const ToolbarItemButtonWrapper = styled('div')(({ theme }) => ({
    '& [class*="MuiButton-root"]': {
        fontSize: '1.3rem',
        lineHeight: '1rem',
        fontWeight: theme.typography.fontWeightBold,
        color: 'inherit',
        textDecoration: 'none',
        paddingLeft: theme.spacing(2.5),
        paddingRight: theme.spacing(2.5),
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
    },
}));

const StyledNavMenu = styled(NavMenu)(({ theme }) => ({
    fontSize: '1.3rem',
    lineHeight: '1rem',
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
}));

const Grow = styled('div')({
    flexGrow: 1,
});

const StyledBadge = styled(Badge)({
    '& [class*="MuiBadge-badge"]': {
        lineHeight: 0,
    },
});

interface HeaderProps {
    overlayAppbar?: boolean;
}

const Header: React.FC<HeaderProps> = ({ overlayAppbar }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMedium = useMediaQuery(theme.breakpoints.down('lg'));
    const { hasPrivilege } = useAccount();
    const { t } = useTranslation();

    return (
        <header>
            <TopBar />
            <AppbarContainer>
                <AppBar
                    component="div"
                    position={overlayAppbar ? 'absolute' : 'sticky'}
                    color="transparent"
                    variant="outlined">
                    <Container>
                        <StyledToolbar>
                            <ToolbarItemLogoWrapper>
                                <Button disableRipple component={RouterLink} to={ROUTE_HOME.path}>
                                    <Logo styled full />
                                </Button>
                            </ToolbarItemLogoWrapper>
                            <Grow />
                            {hasPrivilege('ADMIN') && (
                                <Button disableRipple component={RouterLink} to={ROUTE_ADMIN.path}>
                                    {t('routes:adminpanel.name')}
                                </Button>
                            )}

                            <HomeRoutes asDrawer={isMobile} />
                            <CartRoute />
                            <AccountRoutes asDrawer={isMedium} />
                        </StyledToolbar>
                    </Container>
                </AppBar>
            </AppbarContainer>
        </header>
    );
};

export default Header;

interface HomeRoutesProps {
    asDrawer: boolean;
}

const HomeRoutes: React.FC<HomeRoutesProps> = ({ asDrawer }) => {
    const { t } = useTranslation();
    const { routes: storeRoutes } = useStoreRoutes();
    const enabledModules = useEnabledSiteModuleKeys();

    const routes = useMemo(() => {
        const routeConfig: (AppRoute | AppRoute[])[] = [];

        if (enabledModules.has('home')) {
            routeConfig.push(ROUTE_HOME);
        }

        if (enabledModules.has('store')) {
            routeConfig.push([ROUTE_STORE, ...storeRoutes]);
        }

        if (enabledModules.has('about')) {
            routeConfig.push([ROUTE_ABOUT, ...GROUP_ABOUT]);
        }

        if (enabledModules.has('services')) {
            routeConfig.push([ROUTE_SERVICES, ...GROUP_SERVICES]);
        }

        if (enabledModules.has('contact')) {
            routeConfig.push(ROUTE_CONTACT);
        }

        return routeConfig;
    }, [enabledModules, storeRoutes]);

    if (asDrawer) return <MobileDrawer routes={routes} icon={<MenuIcon />} />;
    else
        return (
            <>
                {routes.map((route, i) => (
                    <React.Fragment key={i}>
                        {Array.isArray(route) ? (
                            <StyledNavMenu
                                title={t(route[0].t_name)}
                                items={route.slice(1)}
                                showDiscovery={route[0].path === ROUTE_STORE.path}
                            />
                        ) : (
                            <ToolbarItemButtonWrapper>
                                <Button component={RouterLink} to={route.path}>
                                    {t(route.t_name)}
                                </Button>
                            </ToolbarItemButtonWrapper>
                        )}
                    </React.Fragment>
                ))}
            </>
        );
};

const CartRoute: React.FC = () => {
    const shoppingCart = useSelector(getShoppingCart);
    const myOffers = useSelector(getMyOffers);

    const offerCount = myOffers.items.length;
    const cartCount = shoppingCart.content?.itemCount ?? 0;

    return (
        <IconButton
            {...ROUTE_CART.componentProps}
            style={{ marginTop: 'auto', marginBottom: 'auto', marginRight: '4px' }}
            color="inherit">
            <StyledBadge color="primary" badgeContent={cartCount > 0 ? cartCount : offerCount}>
                {offerCount > 0 ? <StorefrontOutlinedIcon /> : <ShoppingCartOutlinedIcon />}
            </StyledBadge>
        </IconButton>
    );
};

interface AccountRoutesProps {
    asDrawer: boolean;
}

const AccountRoutes: React.FC<AccountRoutesProps> = ({ asDrawer }) => {
    const { routes: accountRoutes } = useAccountRoutes();
    const account = useAccount();
    const accountActionCount = useAccountActionCount();

    const routes = useMemo(
        () =>
            accountRoutes.filter(r => {
                if (r instanceof AppRoute)
                    return r.accessPrivilege === undefined || account.hasPrivilege(r.accessPrivilege);
                else if (Array.isArray(r) && r.length > 0)
                    return r[0].accessPrivilege === undefined || account.hasPrivilege(r[0].accessPrivilege);
                return false;
            }),
        [account, accountRoutes]
    );

    if (!account.isLoggedIn) return null;

    const actionBadge = (
        <StyledBadge color="primary" badgeContent={accountActionCount}>
            <PersonOutlineOutlinedIcon />
        </StyledBadge>
    );

    if (asDrawer) return <MobileDrawer routes={routes} icon={actionBadge} />;
    else
        return (
            <IconButton
                {...ROUTE_ACCOUNT.componentProps}
                color="inherit"
                style={{ marginTop: 'auto', marginBottom: 'auto' }}>
                {actionBadge}
            </IconButton>
        );
};
